import {
  QButton,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentApi } from '../../../../../api/document';
import {
  DocumentApprovalType,
  DocumentStatus,
} from '../../../../../api/model/document';
import { getApiErrorData } from '../../../../../util/ApiErrorUtils';
import { ButtonGroupContext } from '../../../Contexts/ButtonGroupContext';
import { DocumentOverviewContext } from '../../Context';
import { shouldRenderSendForApprovalButton } from '../../RenderLogic';
import { useSendForSequentialApproval } from './useSendForSequentialApproval';

const CHANGE_CONTROL_REQUIRED_SLUG = 'change-control-required';

export const SendForApprovalButton = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { qualioDocument, refetchDocumentOverviewData, currentEditorMode } =
    useContext(DocumentOverviewContext);
  const { isDisabled, setDisabled } = useContext(ButtonGroupContext);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToastProvider();
  const { sendForSequentialApproval } = useSendForSequentialApproval();

  const commonToastProps = {
    id: 'send-for-approval-toast',
    replace: true,
  };

  const DEFAULT_SEND_FOR_APPROVAL_ERROR_MESSAGE = t(
    'documentActions.failedToSendForApproval',
  );
  const EVENT_FIELD_MISSING_BE_MESSAGE = t(
    'documentActions.cannotSendForApprovalMissingFields',
  );
  const EVENT_FIELD_MISSING_FE_MESSAGE = (
    <span>
      {t('documentActions.missingRequiredFieldsEvent')}{' '}
      <a href={`/quality-events/events/${qualioDocument.issue_id}`}>
        {t('documentActions.clickToViewRequiredEventFields')}
      </a>
    </span>
  );

  const showErrorToast = (messageString: string) => {
    const message =
      messageString === EVENT_FIELD_MISSING_BE_MESSAGE
        ? EVENT_FIELD_MISSING_FE_MESSAGE
        : messageString;

    showToast({
      'data-cy': 'send-for-approval-button-notify-error-toast',
      ...commonToastProps,
      status: 'error',
      title: t('notifications.error'),
      description: message,
    });
  };

  const validateQualityApprovers = () => {
    const { audit_groups: { approver_group } = {} } = qualioDocument;
    const isQualityOptional =
      qualioDocument.issue?.issue_step_quality_approval_required === false;

    if (isQualityOptional) {
      if (!approver_group?.length) {
        throw new Error(t('errors.documentHasNoApprovers'));
      }
      return;
    }

    const hasQualityApprover = approver_group?.some(({ is_qm }) => is_qm);
    if (!hasQualityApprover) {
      throw new Error(t('errors.documentHasNoQualityApprovers'));
    }
  };

  const isSequentialApproval =
    qualioDocument.approval_type === DocumentApprovalType.Sequential;

  const sendForApproval = () =>
    isSequentialApproval
      ? sendForSequentialApproval(qualioDocument.id)
      : documentApi.changeStatus(qualioDocument.id, {
          status: DocumentStatus.For_Approval,
        });

  const handleSendForApproval = async () => {
    if (!isSequentialApproval) {
      try {
        validateQualityApprovers();
      } catch (e: unknown) {
        showErrorToast(
          e instanceof Error
            ? e.message
            : DEFAULT_SEND_FOR_APPROVAL_ERROR_MESSAGE,
        );
        return;
      }
    }

    try {
      setDisabled(true, setIsLoading);
      await sendForApproval();
      setDisabled(false, setIsLoading);
      refetchDocumentOverviewData();
      showToast({
        'data-cy': 'send-for-approval-button-notify-success-toast',
        ...commonToastProps,
        description: t('success.successfullySentForApproval'),
        status: 'success',
        title: t('notifications.sentForApproval'),
      });
    } catch (e: unknown) {
      setDisabled(false, setIsLoading);
      const { message: apiErrorMessage, slug } = getApiErrorData(e);
      if (slug === CHANGE_CONTROL_REQUIRED_SLUG) {
        showToast({
          'data-cy': 'send-for-approval-button-notify-error-toast',
          ...commonToastProps,
          status: 'error',
          title: t('documentActions.changeControlRequired'),
          description: t(
            'documentActions.pleaseCompleteChangeControlBeforeApproval',
          ),
        });
        return;
      }

      showErrorToast(
        apiErrorMessage || DEFAULT_SEND_FOR_APPROVAL_ERROR_MESSAGE,
      );
    }
  };

  if (
    !shouldRenderSendForApprovalButton(
      qualioDocument,
      currentUser,
      currentEditorMode,
    )
  ) {
    return null;
  }

  return (
    <QButton
      isLoading={isLoading}
      isDisabled={isDisabled}
      onClick={handleSendForApproval}
      data-cy="doc-overview-send-for-approval-button"
    >
      {t('buttons.sendForApproval')}
    </QButton>
  );
};
